/**
 * The RFC 7807 error body this backend returns.
 *
 * @see https://datatracker.ietf.org/doc/html/rfc7807#section-3.1
 * @see app.timora.shared.web.errors.ProblemDetailWithCause (the Java side)
 */
export type ProblemDetails = {
  /** A URI reference identifying the problem type, e.g. `https://www.timora.app/problem/constraint-violation`. */
  type?: string;
  /** A short, human-readable summary of the problem type. */
  title?: string;
  /** The HTTP status code. */
  status?: number;
  /** Human-readable explanation of this specific occurrence — what the UI falls back to showing. */
  detail?: string;
  /** A URI reference identifying the specific occurrence. */
  instance?: string;

  /**
   * The stable `errorKey` the backend sets (`Constants.Error.*`). This, not `detail`, is what a client
   * branches on — `detail` is prose meant for logs and may be reworded at any time.
   */
  message?: string;
  /** Parameter that accompanies `message`. */
  params?: string;
  /** The request path. */
  path?: string;
  /** One entry per rejected bean-validation constraint. */
  fieldErrors?: FieldErrorVM[];
};

export type FieldErrorVM = { message: string; objectName: string; field: string };

/**
 * Does this response body look like a problem document?
 *
 * **Matched on SHAPE — never compare the `type` URI here.** An earlier version of this guard did, against
 * one hard-coded URI, and it returned false for every real response: the `fieldErrors` and `message`
 * branches of `notification-middleware` were unreachable in production for as long as it stood.
 *
 * One URI can never be enough. Validation failures — the only responses that carry `fieldErrors` — arrive
 * as `.../problem/constraint-violation`, while everything else arrives as `.../problem-with-message` (see
 * `ExceptionControllerAdvice.getMappedType`), and any type the backend adds later would fall through the
 * same way. The shape is the contract; the URI is documentation.
 *
 * `status` is the discriminator because `ProblemDetail` always carries it. This only ever runs on the
 * body of a rejected request, so a successful payload that happens to have a `status` field of its
 * own (a booking's `status` is a string, not a number) never reaches it.
 */
export const isProblemDetails = (data: unknown): data is ProblemDetails =>
  !!data && typeof data === 'object' && !Array.isArray(data) && typeof (data as ProblemDetails).status === 'number';
